import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  User,
  Phone,
  Mail,
  FileText,
  Paperclip,
  Download,
  Palette,
  X,
  ChevronRight,
  Calendar,
  Sparkles,
  Send,
  Building,
  RefreshCw,
  Clock,
  RotateCcw,
  Factory
} from "lucide-react";
import { toast } from "sonner";
import { api, downloadDocument } from "../../lib/api";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import { compactMoney, money, testid } from "../../lib/format";
import { useSavedState } from "../../lib/listState";
import MoveBackStageModal from "./MoveBackStageModal";

const STAGES = [
  { id: "New", label: "New", description: "Orders assigned to Design awaiting start", color: "#0284c7", bg: "#f0f9ff", border: "#bae6fd", badge: "blue" },
  { id: "In Progress", label: "In Progress", description: "Designer actively working on artwork", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", badge: "purple" },
  { id: "Awaiting Approval", label: "Awaiting Approval", description: "Design submitted for review", color: "#d97706", bg: "#fffbeb", border: "#fde68a", badge: "amber" },
  { id: "Approved", label: "Approved", description: "Design signed off - Ready for Production", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0", badge: "green" },
];

export default function Design({ orders: propOrders, clients, onOpen, onOpenDesign, onChange, user }) {
  const [boardData, setBoardData] = useState({ orders: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useSavedState("design", "search", "");
  const [stageFilter, setStageFilter] = useSavedState("design", "stageFilter", "");
  const [designerFilter, setDesignerFilter] = useSavedState("design", "designerFilter", "");
  const [priorityFilter, setPriorityFilter] = useSavedState("design", "priorityFilter", "");
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [busyOrder, setBusyOrder] = useState("");

  // Modals state
  const [assignModalOrder, setAssignModalOrder] = useState(null);
  const [vendorModalOrder, setVendorModalOrder] = useState(null);
  const [moveBackOrder, setMoveBackOrder] = useState(null);
  const [designersList, setDesignersList] = useState([]);
  const [vendorsList, setVendorsList] = useState([]);

  const canEdit = isAdmin(user) || canAccessDepartment(user, ["Sales", "Design"]);

  // Fetch team designers and production vendors
  const loadDesignersAndVendors = useCallback(async () => {
    try {
      const dRes = await api("get", "/users?role=design");
      if (dRes?.data) setDesignersList(dRes.data);
    } catch (_) {}

    try {
      const vRes = await api("get", "/vendors?status=Active");
      if (vRes?.data && vRes.data.length > 0) {
        setVendorsList(vRes.data.map((v) => ({ email: v.vendor_id, name: v.name, ...v })));
      } else {
        const uRes = await api("get", "/users?role=production");
        setVendorsList(uRes?.data || []);
      }
    } catch (_) {
      try {
        const uRes = await api("get", "/users?role=production");
        setVendorsList(uRes?.data || []);
      } catch (_) {}
    }
  }, []);

  // Fetch enriched design board data from backend
  const loadBoard = useCallback(async (isManual = false) => {
    try {
      setLoading(true);
      const [boardRes] = await Promise.all([
        api("get", "/designs/board"),
        loadDesignersAndVendors(),
      ]);
      if (boardRes?.data) {
        setBoardData(boardRes.data);
      }
      if (onChange) {
        onChange();
      }
      if (isManual) {
        toast.success("Design board refreshed");
      }
    } catch (err) {
      console.error("Failed to load design board:", err);
      toast.error("Could not load design board");
    } finally {
      setLoading(false);
    }
  }, [loadDesignersAndVendors, onChange]);

  // Initial load
  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  // Update single order stage via backend
  const updateStage = async (orderId, targetStage, extraData = {}) => {
    setBusyOrder(orderId);
    try {
      await api("patch", `/orders/${orderId}/design-stage`, {
        design_stage: targetStage,
        ...extraData,
      });
      toast.success(`Moved to ${targetStage}`);
      await loadBoard();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not update stage";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusyOrder("");
    }
  };

  // Update complete bulk batch stage via backend
  const updateBatchStage = async (batchId, targetStage, extraData = {}) => {
    setBusyOrder(batchId);
    try {
      await api("patch", `/orders/batch/${batchId}/design-stage`, {
        design_stage: targetStage,
        ...extraData,
      });
      toast.success(`Bulk Request #${batchId} moved to ${targetStage}`);
      await loadBoard();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not update batch stage";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusyOrder("");
    }
  };

  // Bulk Batch Start
  const handleBatchStartDesign = async (batchId) => {
    await updateBatchStage(batchId, "In Progress", { remarks: "Bulk start: all products moved to In Progress" });
  };

  // Bulk Batch Submit for Approval (Immediate direct submission without modal popup)
  const handleBatchSubmitApproval = async (batchId) => {
    await updateBatchStage(batchId, "Awaiting Approval", {
      remarks: "Bulk design submitted for approval",
      version_comments: "Bulk artwork proof submitted for approval",
    });
  };

  // Bulk Batch Approve
  const handleBatchApprove = async (batchId, comments = "") => {
    setBusyOrder(batchId);
    try {
      await api("post", `/orders/batch/${batchId}/approvals`, {
        status: "Approved",
        comments: comments || "Bulk Design Approved",
      });
      toast.success(`Bulk Request #${batchId} approved (All products)`);
      await loadBoard();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not approve bulk request";
      toast.error(typeof msg === "string" ? msg : "Approval failed");
    } finally {
      setBusyOrder("");
    }
  };

  // Bulk Batch Request Revision
  const handleBatchRequestRevision = async (batchId, comments = "") => {
    setBusyOrder(batchId);
    try {
      await api("post", `/orders/batch/${batchId}/approvals`, {
        status: "Not Approved",
        comments: comments || "Bulk Design Revisions Requested",
      });
      toast.success(`Bulk Request #${batchId} returned for revision (In Progress)`);
      await loadBoard();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not request revision";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setBusyOrder("");
    }
  };

  // Bulk Batch Request Production
  const handleBatchRequestProduction = async (batchId, remarks = "") => {
    setBusyOrder(batchId);
    try {
      await api("post", `/orders/batch/${batchId}/request-production`, {
        remarks: remarks || "Production details requested for bulk batch",
      });
      toast.success(`Production details requested for all products in Bulk Request #${batchId}`);
      await loadBoard();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not request production details";
      toast.error(typeof msg === "string" ? msg : "Request failed");
    } finally {
      setBusyOrder("");
    }
  };

  // Bulk Batch Send to Production
  const handleBatchSendToProduction = async (batchId) => {
    setBusyOrder(batchId);
    try {
      await api("post", `/orders/batch/${batchId}/send-to-production`);
      toast.success(`Bulk Request #${batchId} sent to Production queue (Not Started)`);
      await loadBoard();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not send bulk request to production";
      toast.error(typeof msg === "string" ? msg : "Send failed");
      loadBoard();
    } finally {
      setBusyOrder("");
    }
  };

  // Request Production Vendor & Warehouse for single order
  const handleRequestProduction = async (orderId) => {
    setBusyOrder(orderId);
    try {
      await api("post", `/orders/${orderId}/request-production`);
      toast.success("Production details requested from Production team");
      await loadBoard();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not request production details";
      toast.error(typeof msg === "string" ? msg : "Request failed");
    } finally {
      setBusyOrder("");
    }
  };

  // Officially Send to Production for single order (after details confirmed)
  const handleSendToProduction = async (orderId) => {
    setBusyOrder(orderId);

    // Optimistic instant removal from Design Pipeline
    setBoardData((prev) => ({
      ...prev,
      orders: (prev.orders || []).filter((o) => o.order_id !== orderId),
      counts: {
        ...prev.counts,
        total: Math.max(0, (prev.counts?.total || 1) - 1),
        Approved: Math.max(0, (prev.counts?.Approved || 1) - 1),
      },
    }));

    try {
      await api("post", `/orders/${orderId}/send-to-production`);
      toast.success("Order sent to Production queue (Not Started)");
      await loadBoard();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not send to production";
      toast.error(typeof msg === "string" ? msg : "Send failed");
      loadBoard();
    } finally {
      setBusyOrder("");
    }
  };

  // Handle Drag & Drop
  const handleDrop = (targetStage) => {
    if (!dragging || !canEdit) return;
    const item = dragging;
    setDragging(null);
    setDropTarget(null);

    if (item.isBatch) {
      if (item.primaryStage === targetStage) return;
      if (targetStage === "Approved") {
        handleBatchApprove(item.batch_id);
        return;
      }
      if (targetStage === "Awaiting Approval") {
        handleBatchSubmitApproval(item.batch_id);
        return;
      }
      updateBatchStage(item.batch_id, targetStage);
      return;
    }

    if (item.design_stage === targetStage) return;

    if (targetStage === "Approved") {
      updateStage(item.order_id, "Approved", { status: "Approved" });
      return;
    }

    if (targetStage === "Awaiting Approval") {
      updateStage(item.order_id, "Awaiting Approval");
      return;
    }

    // For "In Progress" stage, auto-assign designer if not already assigned
    // Backend will handle auto-assignment to current user
    updateStage(item.order_id, targetStage);
  };

  // Filter orders
  // Pipeline metrics across all active design orders
  const designMetrics = useMemo(() => {
    const validStages = ["New", "In Progress", "Awaiting Approval", "Approved"];
    const orders = (boardData.orders || []).filter(
      (o) =>
        !o.deleted &&
        o.design_stage !== "Sent to Production" &&
        !["Production", "Dispatch", "Challan", "Delivery", "Delivered", "Accounts", "Invoice", "Payment", "Completed", "Cancelled"].includes(o.current_stage) &&
        validStages.includes(o.design_stage || "New")
    );
    return {
      total: orders.length,
      newOrders: orders.filter((o) => (o.design_stage || "New") === "New").length,
      inProgress: orders.filter((o) => o.design_stage === "In Progress").length,
      awaitingApproval: orders.filter((o) => o.design_stage === "Awaiting Approval").length,
      approved: orders.filter((o) => o.design_stage === "Approved").length,
    };
  }, [boardData.orders]);

  // Group board orders into standalone single orders and grouped bulk batches
  const groupedBoardItems = useMemo(() => {
    const rawOrders = (boardData.orders || []).filter(
      (o) =>
        !o.deleted &&
        o.design_stage !== "Sent to Production" &&
        !["Production", "Dispatch", "Challan", "Delivery", "Delivered", "Accounts", "Invoice", "Payment", "Completed", "Cancelled"].includes(o.current_stage)
    );

    const batchMap = new Map();
    const singles = [];

    rawOrders.forEach((o) => {
      const batchId = o.upload_batch_id;
      if (batchId) {
        if (!batchMap.has(batchId)) {
          batchMap.set(batchId, []);
        }
        batchMap.get(batchId).push(o);
      } else {
        singles.push(o);
      }
    });

    const items = [...singles];

    batchMap.forEach((batchOrders, batchId) => {
      if (batchOrders.length === 1 && !batchOrders[0].is_bulk_upload) {
        items.push(batchOrders[0]);
        return;
      }

      const first = batchOrders[0];
      const counts = {
        total: batchOrders.length,
        New: batchOrders.filter((o) => (o.design_stage || "New") === "New").length,
        "In Progress": batchOrders.filter((o) => o.design_stage === "In Progress").length,
        "Awaiting Approval": batchOrders.filter((o) => o.design_stage === "Awaiting Approval").length,
        Approved: batchOrders.filter((o) => o.design_stage === "Approved").length,
      };

      // Primary Kanban column for the batch card
      let primaryStage = "New";
      if (counts.Approved === counts.total) {
        primaryStage = "Approved";
      } else if (counts["In Progress"] > 0) {
        primaryStage = "In Progress";
      } else if (counts["Awaiting Approval"] > 0) {
        primaryStage = "Awaiting Approval";
      } else {
        primaryStage = "New";
      }

      const priority = batchOrders.some((o) => o.priority === "Urgent")
        ? "Urgent"
        : batchOrders.some((o) => o.priority === "High")
        ? "High"
        : "Normal";

      const allConfirmed = batchOrders.length > 0 && batchOrders.every(
        (o) => o.production_request_status === "confirmed" && Boolean(o.production_vendor_name || o.assigned_producer || o.warehouse_name)
      );
      const anyRequested = batchOrders.some(
        (o) => o.production_request_status === "requested" || o.production_request_status === "pending"
      );

      let prodReqStatus = "none";
      let vendorName = "";
      let vendorId = "";
      let warehouseName = "";
      let warehouseId = "";
      let confirmedBy = "";
      let confirmedAt = "";
      let requestedBy = batchOrders.find((o) => o.production_requested_by)?.production_requested_by || "";
      let requestedAt = batchOrders.find((o) => o.production_requested_at)?.production_requested_at || "";
      let approvedBy = batchOrders.find((o) => o.approved_by)?.approved_by || batchOrders.find((o) => o.latest_approval?.approver)?.latest_approval?.approver || "";
      let approvedAt = batchOrders.find((o) => o.approved_at)?.approved_at || batchOrders.find((o) => o.latest_approval?.created_at)?.latest_approval?.created_at || "";

      if (allConfirmed) {
        prodReqStatus = "confirmed";
        vendorName = batchOrders.find((o) => o.production_vendor_name)?.production_vendor_name ||
          batchOrders.find((o) => o.assigned_producer)?.assigned_producer || "";
        vendorId = batchOrders.find((o) => o.production_vendor_id)?.production_vendor_id || "";
        warehouseName = batchOrders.find((o) => o.warehouse_name)?.warehouse_name || "";
        warehouseId = batchOrders.find((o) => o.warehouse_id)?.warehouse_id || "";
        confirmedBy = batchOrders.find((o) => o.production_confirmed_by)?.production_confirmed_by || "";
        confirmedAt = batchOrders.find((o) => o.production_confirmed_at)?.production_confirmed_at || "";
      } else if (anyRequested) {
        prodReqStatus = "requested";
      }

      items.push({
        isBatch: true,
        batch_id: batchId,
        batch_name: first.upload_batch_name || `Bulk Design Request · ${batchOrders.length} Products`,
        client_name: first.client_name,
        client_id: first.client_id,
        client_contact_person: first.client_contact_person,
        client_phone: first.client_phone,
        client_email: first.client_email,
        priority,
        total_value: batchOrders.reduce((sum, o) => sum + (o.total_value || 0), 0),
        orders: batchOrders,
        counts,
        primaryStage,
        design_stage: primaryStage,
        created_at: first.created_at,
        production_request_status: prodReqStatus,
        production_vendor_name: vendorName,
        production_vendor_id: vendorId,
        warehouse_name: warehouseName,
        warehouse_id: warehouseId,
        production_confirmed_by: confirmedBy,
        production_confirmed_at: confirmedAt,
        production_requested_by: requestedBy,
        production_requested_at: requestedAt,
        approved_by: approvedBy,
        approved_at: approvedAt,
      });
    });

    return items;
  }, [boardData.orders]);

  const filteredItems = useMemo(() => {
    return groupedBoardItems.filter((item) => {
      if (item.isBatch) {
        if (stageFilter) {
          const hasStage = item.primaryStage === stageFilter || item.orders.some((o) => (o.design_stage || "New") === stageFilter);
          if (!hasStage) return false;
        }
        if (priorityFilter && item.priority !== priorityFilter) return false;
        if (designerFilter) {
          if (designerFilter === "unassigned") {
            const hasUnassigned = item.orders.some((o) => !o.assigned_designer);
            if (!hasUnassigned) return false;
          } else {
            const hasDesigner = item.orders.some((o) => o.assigned_designer?.toLowerCase() === designerFilter.toLowerCase());
            if (!hasDesigner) return false;
          }
        }
        if (search) {
          const q = search.toLowerCase();
          const match =
            item.batch_id.toLowerCase().includes(q) ||
            item.batch_name.toLowerCase().includes(q) ||
            item.client_name.toLowerCase().includes(q) ||
            item.client_contact_person?.toLowerCase().includes(q) ||
            item.orders.some(
              (o) =>
                o.order_id.toLowerCase().includes(q) ||
                o.product.toLowerCase().includes(q) ||
                o.assigned_designer?.toLowerCase().includes(q) ||
                o.notes?.toLowerCase().includes(q)
            );
          if (!match) return false;
        }
        return true;
      }

      // Standalone single order
      const validStages = ["New", "In Progress", "Awaiting Approval", "Approved"];
      const st = item.design_stage || "New";
      if (!validStages.includes(st)) return false;

      if (stageFilter && st !== stageFilter) return false;
      if (priorityFilter && item.priority !== priorityFilter) return false;
      if (designerFilter) {
        if (designerFilter === "unassigned") {
          if (item.assigned_designer) return false;
        } else if (item.assigned_designer?.toLowerCase() !== designerFilter.toLowerCase()) {
          return false;
        }
      }
      if (search) {
        const q = search.toLowerCase();
        const match =
          item.order_id?.toLowerCase().includes(q) ||
          item.client_name?.toLowerCase().includes(q) ||
          item.product?.toLowerCase().includes(q) ||
          item.assigned_designer?.toLowerCase().includes(q) ||
          item.client_contact_person?.toLowerCase().includes(q) ||
          item.notes?.toLowerCase().includes(q);
        if (!match) return false;
      }
      return true;
    });
  }, [groupedBoardItems, search, stageFilter, designerFilter, priorityFilter]);

  // Group by stages
  const columns = useMemo(() => {
    const map = {};
    STAGES.forEach((s) => (map[s.id] = []));
    filteredItems.forEach((item) => {
      const st = item.isBatch ? item.primaryStage : (item.design_stage || "New");
      if (map[st]) {
        map[st].push(item);
      } else if (map["New"]) {
        map["New"].push(item);
      }
    });
    return map;
  }, [filteredItems]);

  // Eligible designers for filter (strictly users assigned to Design department)
  const uniqueDesigners = useMemo(() => {
    const set = new Set();
    designersList.forEach((u) => u.name && set.add(u.name));
    return Array.from(set);
  }, [designersList]);

  return (
    <div className="content design-board-content">
      {/* Page Heading */}
      <div className="page-heading">
        <div>
          <div className="small-label">WORKFLOW PIPELINE</div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Palette size={26} color="#0284c7" /> Design Pipeline
          </h1>
          <p className="muted">
            Track artwork progression from intake to approval. Send to Production once design is approved.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            className="outline-btn compact"
            onClick={() => loadBoard(true)}
            disabled={loading}
            title="Refresh Board"
            data-testid="design-refresh-button"
          >
            <RefreshCw size={14} className={loading ? "spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Metrics Row (Interactive Filters) */}
      <div className="metrics-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        <div
          className={`metric blue`}
          data-testid="metric-total-design"
          onClick={() => setStageFilter("")}
          style={{
            cursor: "pointer",
            userSelect: "none",
            ...(!stageFilter ? { outline: "2px solid var(--navy)", outlineOffset: "-1px" } : {})
          }}
          title="Show all design stages"
        >
          <div className="metric-top">
            <span>Total in Pipeline</span>
            {!stageFilter && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy)" }}>ALL</span>}
          </div>
          <strong>{designMetrics.total}</strong>
        </div>

        <div
          className={`metric blue`}
          data-testid="metric-new-design"
          onClick={() => setStageFilter(stageFilter === "New" ? "" : "New")}
          style={{
            cursor: "pointer",
            userSelect: "none",
            ...(stageFilter === "New" ? { outline: "2px solid var(--navy)", outlineOffset: "-1px" } : {})
          }}
          title="Filter by New"
        >
          <div className="metric-top">
            <span>New</span>
            {stageFilter === "New" && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy)" }}>FILTERED</span>}
          </div>
          <strong>{designMetrics.newOrders}</strong>
        </div>

        <div
          className={`metric purple`}
          data-testid="metric-in-progress"
          onClick={() => setStageFilter(stageFilter === "In Progress" ? "" : "In Progress")}
          style={{
            cursor: "pointer",
            userSelect: "none",
            ...(stageFilter === "In Progress" ? { outline: "2px solid var(--navy)", outlineOffset: "-1px" } : {})
          }}
          title="Filter by In Progress"
        >
          <div className="metric-top">
            <span>In Progress</span>
            {stageFilter === "In Progress" && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy)" }}>FILTERED</span>}
          </div>
          <strong>{designMetrics.inProgress}</strong>
        </div>

        <div
          className={`metric amber`}
          data-testid="metric-awaiting-approval"
          onClick={() => setStageFilter(stageFilter === "Awaiting Approval" ? "" : "Awaiting Approval")}
          style={{
            cursor: "pointer",
            userSelect: "none",
            ...(stageFilter === "Awaiting Approval" ? { outline: "2px solid var(--navy)", outlineOffset: "-1px" } : {})
          }}
          title="Filter by Awaiting Approval"
        >
          <div className="metric-top">
            <span>Awaiting Approval</span>
            {stageFilter === "Awaiting Approval" && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy)" }}>FILTERED</span>}
          </div>
          <strong>{designMetrics.awaitingApproval}</strong>
        </div>

        <div
          className={`metric green`}
          data-testid="metric-approved"
          onClick={() => setStageFilter(stageFilter === "Approved" ? "" : "Approved")}
          style={{
            cursor: "pointer",
            userSelect: "none",
            ...(stageFilter === "Approved" ? { outline: "2px solid var(--navy)", outlineOffset: "-1px" } : {})
          }}
          title="Filter by Approved"
        >
          <div className="metric-top">
            <span>Approved & Ready</span>
            {stageFilter === "Approved" && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy)" }}>FILTERED</span>}
          </div>
          <strong>{designMetrics.approved}</strong>
        </div>
      </div>

      {/* Toolbar: Search & Filters */}
      <div className="toolbar" style={{ marginTop: 14, marginBottom: 16 }}>
        <div className="search-box" style={{ flex: 1 }}>
          <Search size={16} />
          <input
            placeholder="Search Order ID, client, contact, product, or designer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="design-search-input"
          />
          {search && (
            <button className="icon-btn" onClick={() => setSearch("")} style={{ padding: 2 }} title="Clear search">
              <X size={14} />
            </button>
          )}
        </div>

        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          data-testid="design-filter-stage"
          style={{ minWidth: 140 }}
        >
          <option value="">All Stages</option>
          {STAGES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>

        <select
          value={designerFilter}
          onChange={(e) => setDesignerFilter(e.target.value)}
          data-testid="design-filter-designer"
          style={{ minWidth: 160 }}
        >
          <option value="">All Designers</option>
          <option value="unassigned">Unassigned</option>
          {uniqueDesigners.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          data-testid="design-filter-priority"
          style={{ minWidth: 130 }}
        >
          <option value="">All Priorities</option>
          <option value="Urgent">Urgent</option>
          <option value="High">High</option>
          <option value="Normal">Normal</option>
        </select>
      </div>

      {/* Kanban Board Grid */}
      <div className="design-kanban-board" data-testid="design-kanban-board">
        {STAGES.filter((stage) => !stageFilter || stage.id === stageFilter).map((stage) => (
          <DesignKanbanColumn
            key={stage.id}
            stage={stage}
            cards={columns[stage.id] || []}
            isDropActive={dropTarget === stage.id}
            canEdit={canEdit}
            dragging={dragging}
            setDropTarget={setDropTarget}
            handleDrop={handleDrop}
            user={user}
            busyOrder={busyOrder}
            setDragging={setDragging}
            onOpen={onOpen}
            onOpenDesign={onOpenDesign}
            setAssignModalOrder={setAssignModalOrder}
            updateStage={updateStage}
            onBatchSubmitApproval={handleBatchSubmitApproval}
            onRequestProduction={handleRequestProduction}
            onSendToProduction={handleSendToProduction}
            onBatchStartDesign={handleBatchStartDesign}
            onBatchApprove={handleBatchApprove}
            onBatchRequestRevision={handleBatchRequestRevision}
            onBatchRequestProduction={handleBatchRequestProduction}
            onBatchSendToProduction={handleBatchSendToProduction}
            onBatchMoveBack={(batch) => setMoveBackOrder(batch)}
            onMoveBackSingle={(order) => setMoveBackOrder(order)}
          />
        ))}
      </div>

      {/* Assign Designer Modal */}
      {assignModalOrder && (
        <AssignDesignerDialog
          order={assignModalOrder}
          designers={designersList}
          user={user}
          onClose={() => setAssignModalOrder(null)}
          onConfirm={(designerName) => {
            if (assignModalOrder.isBatch) {
              const bId = assignModalOrder.batch_id;
              setAssignModalOrder(null);
              updateBatchStage(bId, assignModalOrder.primaryStage || "In Progress", { assigned_designer: designerName });
            } else {
              const oid = assignModalOrder.order_id;
              const pendingStage = assignModalOrder.pendingStage;
              setAssignModalOrder(null);
              if (pendingStage) {
                updateStage(oid, pendingStage, { assigned_designer: designerName });
              } else {
                updateStage(oid, assignModalOrder.design_stage || "In Progress", { assigned_designer: designerName });
              }
            }
          }}
        />
      )}

      {/* Move Back Modal */}
      {moveBackOrder && (
        <MoveBackStageModal
          order={moveBackOrder}
          isOpen={Boolean(moveBackOrder)}
          onClose={() => setMoveBackOrder(null)}
          onSuccess={() => {
            loadBoard();
            onChange && onChange();
          }}
          user={user}
        />
      )}
    </div>
  );
}

function DesignKanbanColumn({
  stage,
  cards,
  isDropActive,
  canEdit,
  dragging,
  setDropTarget,
  handleDrop,
  user,
  busyOrder,
  setDragging,
  onOpen,
  onOpenDesign,
  setAssignModalOrder,
  updateStage,
  onBatchSubmitApproval,
  onRequestProduction,
  onSendToProduction,
  onBatchStartDesign,
  onBatchApprove,
  onBatchRequestRevision,
  onBatchRequestProduction,
  onBatchSendToProduction,
  onBatchMoveBack,
  onMoveBackSingle,
}) {
  const totalVal = cards.reduce((sum, c) => sum + (c.total_value || 0), 0);

  return (
    <div
      className={`design-kanban-col ${isDropActive ? "drop-active" : ""}`}
      style={{
        borderTop: `3px solid ${stage.color}`,
        display: "flex",
        flexDirection: "column",
      }}
      data-testid={`design-column-${testid(stage.id)}`}
      onDragOver={(e) => {
        if (canEdit && dragging) {
          e.preventDefault();
          setDropTarget(stage.id);
        }
      }}
      onDragLeave={() => setDropTarget((t) => (t === stage.id ? null : t))}
      onDrop={() => handleDrop(stage.id)}
    >
      {/* Column Header */}
      <div className="design-col-head">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            className="design-stage-dot"
            style={{ background: stage.color }}
          />
          <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: "var(--navy)" }}>
            {stage.label}
          </h3>
          <span className="count" style={{ fontSize: 11, padding: "2px 7px" }}>
            {cards.length}
          </span>
        </div>
        <small className="muted" style={{ fontWeight: 600, fontSize: 11 }}>
          {compactMoney(totalVal)}
        </small>
      </div>

      {/* Cards Container */}
      <div className="design-cards-list" style={{ flex: 1 }}>
        {cards.map((item) => {
          if (item.isBatch) {
            return (
              <BulkDesignBatchCard
                key={item.batch_id}
                batch={item}
                stage={stage.id}
                canEdit={canEdit}
                user={user}
                isBusy={busyOrder === item.batch_id}
                isDragging={dragging?.batch_id === item.batch_id}
                onDragStart={(e) => {
                  setDragging(item);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragEnd={() => {
                  setDragging(null);
                  setDropTarget(null);
                }}
                onOpenDesign={(batchId) => (onOpenDesign || onOpen)(batchId)}
                onOpenAssign={() => setAssignModalOrder(item)}
                onStartDesign={() => onBatchStartDesign && onBatchStartDesign(item.batch_id)}
                onSubmitApproval={() => onBatchSubmitApproval && onBatchSubmitApproval(item.batch_id)}
                onApprove={() => onBatchApprove && onBatchApprove(item.batch_id)}
                onRequestRevision={() => onBatchRequestRevision && onBatchRequestRevision(item.batch_id)}
                onRequestProduction={() => onBatchRequestProduction && onBatchRequestProduction(item.batch_id)}
                onSendToProduction={() => onBatchSendToProduction && onBatchSendToProduction(item.batch_id)}
                onMoveBack={() => onBatchMoveBack && onBatchMoveBack(item)}
              />
            );
          }
          return (
            <StageSpecificCard
              key={item.order_id}
              order={item}
              stage={stage.id}
              canEdit={canEdit}
              user={user}
              isBusy={busyOrder === item.order_id}
              isDragging={dragging?.order_id === item.order_id}
              onDragStart={(e) => {
                setDragging(item);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => {
                setDragging(null);
                setDropTarget(null);
              }}
              onOpenOrder={() => onOpen(item.order_id)}
              onOpenDesign={() => (onOpenDesign || onOpen)(item.order_id)}
              onOpenAssign={() => setAssignModalOrder(item)}
              onStartDesign={() => {
                updateStage(item.order_id, "In Progress");
              }}
              onSubmitApproval={() => {
                updateStage(item.order_id, "Awaiting Approval");
              }}
              onApprove={() => {
                updateStage(item.order_id, "Approved", { status: "Approved" });
              }}
              onRequestRevision={() => {
                updateStage(item.order_id, "In Progress", {
                  remarks: "Revisions requested (Not Approved)",
                });
              }}
              onRequestProduction={() => {
                onRequestProduction && onRequestProduction(item.order_id);
              }}
              onSendToProduction={() => {
                onSendToProduction && onSendToProduction(item.order_id);
              }}
              onMoveBack={() => {
                onMoveBackSingle && onMoveBackSingle(item);
              }}
            />
          );
        })}

        {cards.length === 0 && (
          <div className="design-col-empty">
            <span className="muted">No orders in this stage</span>
            {canEdit && <small style={{ display: "block", marginTop: 4, color: "#94a3b8" }}>Drop cards here</small>}
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Bulk Upload Design Request / Batch Kanban Card
// -------------------------------------------------------------
// -------------------------------------------------------------
// Bulk Upload Design Request / Batch Kanban Card
// -------------------------------------------------------------
function BulkDesignBatchCard({
  batch,
  stage,
  canEdit,
  user,
  isBusy,
  isDragging,
  onDragStart,
  onDragEnd,
  onOpenDesign,
  onOpenAssign,
  onStartDesign,
  onSubmitApproval,
  onApprove,
  onRequestRevision,
  onRequestProduction,
  onSendToProduction,
  onMoveBack,
}) {
  const isUrgent = batch.priority === "Urgent";
  const isHigh = batch.priority === "High";

  const stageBadgeStyle = (st) => {
    switch (st) {
      case "Approved":
        return { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" };
      case "Awaiting Approval":
        return { color: "#d97706", bg: "#fffbeb", border: "#fde68a" };
      case "In Progress":
        return { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" };
      default:
        return { color: "#0284c7", bg: "#f0f9ff", border: "#bae6fd" };
    }
  };

  const assignedDesigner = batch.assigned_designer || batch.orders.find((o) => o.assigned_designer)?.assigned_designer || "";
  
  const hasVendor = Boolean(batch.production_vendor_name);
  const hasWarehouse = Boolean(batch.warehouse_name);
  const isConfirmedProd = batch.production_request_status === "confirmed" && (hasVendor || hasWarehouse);
  const isRequestedProd = !isConfirmedProd && (
    batch.production_request_status === "requested" ||
    batch.production_request_status === "pending" ||
    batch.orders.some((o) => o.production_request_status === "requested" || o.production_request_status === "pending")
  );
  const isInitialApproved = !isConfirmedProd && !isRequestedProd;

  return (
    <div
      className={`design-kanban-card bulk-batch-card ${isDragging ? "dragging" : ""} ${isBusy ? "busy-card" : ""}`}
      draggable={canEdit && !isBusy}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={() => onOpenDesign(batch.batch_id)}
      style={{
        cursor: "pointer",
        background: "#ffffff",
        border: "1.5px solid #cbd5e1",
        borderRadius: 8,
        padding: "12px 13px",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.05)",
      }}
      data-testid={`bulk-design-batch-card-${batch.batch_id}`}
    >
      {/* Top Row: Batch ID & Priority */}
      <div className="card-top-row" style={{ marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            className="order-id-badge"
            style={{ background: "#eff6ff", color: "#2563eb", borderColor: "#bfdbfe" }}
            title={`Batch ID: ${batch.batch_id}`}
            data-testid={`bulk-batch-id-${batch.batch_id}`}
          >
            #{batch.batch_id}
          </span>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: "#334155",
              background: "#f1f5f9",
              padding: "2px 6px",
              borderRadius: 4,
              border: "1px solid #e2e8f0",
            }}
          >
            Bulk Request · {batch.orders.length} Products
          </span>
        </div>
        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
          <span
            className={`badge ${isUrgent ? "red" : isHigh ? "amber" : "blue"}`}
            style={{ fontSize: 10, padding: "2px 6px" }}
          >
            {batch.priority}
          </span>
        </div>
      </div>

      {/* Client Name */}
      <div style={{ marginBottom: 4 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 700,
            color: "var(--navy)",
            lineHeight: 1.3,
          }}
          title={batch.client_name}
        >
          {batch.client_name}
        </div>
      </div>

      {/* Assigned Designer Row (Clickable to assign across entire bulk request) */}
      <div className="card-designer-row" style={{ marginBottom: 6 }}>
        <small className="muted" style={{ fontSize: 11 }}>Designer:</small>
        <button
          className="designer-pill"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenAssign();
          }}
          title="Click to assign designer to all products in this bulk request"
          data-testid={`bulk-designer-btn-${batch.batch_id}`}
          style={{ fontSize: 11, padding: "2px 8px" }}
        >
          <span className="designer-avatar" style={{ width: 17, height: 17, fontSize: 9.5 }}>
            {(assignedDesigner || "D").charAt(0).toUpperCase()}
          </span>
          <span>{assignedDesigner || "Unassigned"}</span>
        </button>
      </div>

      {/* Individual Products List (Compact & Readable) */}
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 6,
          padding: "5px 8px",
          marginBottom: 6,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 105, overflowY: "auto" }}>
          {batch.orders.map((o) => {
            const prodStage = o.design_stage || "New";
            const badgeTheme = stageBadgeStyle(prodStage);
            return (
              <div
                key={o.order_id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 6,
                  fontSize: 11.5,
                  padding: "2px 0",
                  borderBottom: "1px solid #f1f5f9",
                }}
                data-testid={`batch-product-row-${o.order_id}`}
              >
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }} title={o.product}>
                  <span style={{ fontWeight: 600, color: "var(--navy)" }}>{o.product}</span>
                </div>
                <div style={{ color: "#475569", fontWeight: 600, fontSize: 11, whiteSpace: "nowrap" }}>
                  {o.quantity} {o.unit || "pcs"}
                </div>
                <span
                  style={{
                    fontSize: 9.5,
                    fontWeight: 600,
                    padding: "1px 5px",
                    borderRadius: 3,
                    color: badgeTheme.color,
                    background: badgeTheme.bg,
                    border: `1px solid ${badgeTheme.border}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {prodStage}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stage-Specific Bulk Workflow Actions */}
      {canEdit && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
          {/* STAGE 1: NEW */}
          {stage === "New" && (
            <button
              type="button"
              className="primary-btn wide"
              style={{ fontSize: 11.5, padding: "6px 10px", background: "#0284c7", borderColor: "#0284c7", justifyContent: "center" }}
              onClick={(e) => {
                e.stopPropagation();
                onStartDesign();
              }}
              disabled={isBusy}
              data-testid={`bulk-action-start-${batch.batch_id}`}
            >
              Start Bulk Design <ArrowRight size={13} />
            </button>
          )}

          {/* STAGE 2: IN PROGRESS */}
          {stage === "In Progress" && (
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                className="outline-btn compact"
                style={{ color: "#b45309", borderColor: "#fde68a", background: "#fffbeb", fontSize: 11, padding: "5px 8px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveBack();
                }}
                disabled={isBusy}
                title="Move bulk request back to Order Received"
                data-testid={`bulk-action-move-back-${batch.batch_id}`}
              >
                <RotateCcw size={12} /> Move Back
              </button>
              <button
                type="button"
                className="primary-btn wide"
                style={{ flex: 1, fontSize: 11.5, padding: "6px 10px", background: "#7c3aed", borderColor: "#7c3aed", justifyContent: "center" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSubmitApproval();
                }}
                disabled={isBusy}
                data-testid={`bulk-action-submit-${batch.batch_id}`}
              >
                Submit for Approval <ArrowRight size={13} />
              </button>
            </div>
          )}

          {/* STAGE 3: AWAITING APPROVAL */}
          {stage === "Awaiting Approval" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  className="primary-btn"
                  style={{ flex: 1, fontSize: 11.5, padding: "6px 10px", background: "#16a34a", borderColor: "#16a34a", justifyContent: "center" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onApprove();
                  }}
                  disabled={isBusy}
                  data-testid={`bulk-action-approve-${batch.batch_id}`}
                >
                  <CheckCircle2 size={13} /> Approve (All)
                </button>
                <button
                  type="button"
                  className="outline-btn"
                  style={{ flex: 1, fontSize: 11, padding: "5px 8px", color: "#dc2626", borderColor: "#fca5a5", justifyContent: "center" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRequestRevision();
                  }}
                  disabled={isBusy}
                  data-testid={`bulk-action-revision-${batch.batch_id}`}
                >
                  <RotateCcw size={12} /> Revision
                </button>
              </div>
              <button
                type="button"
                className="outline-btn compact"
                style={{ color: "#b45309", borderColor: "#fde68a", background: "#fffbeb", fontSize: 11, padding: "4px 8px", justifyContent: "center" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveBack();
                }}
                disabled={isBusy}
                data-testid={`bulk-action-move-back-${batch.batch_id}`}
              >
                <RotateCcw size={11} /> Move Bulk Request Back
              </button>
            </div>
          )}

          {/* STAGE 4: APPROVED */}
          {stage === "Approved" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {isConfirmedProd ? (
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "7px 9px", fontSize: 11.5 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#166534", fontWeight: 700, marginBottom: 3 }}>
                    <CheckCircle2 size={13} color="#16a34a" /> Production Details Confirmed
                  </div>
                  <div style={{ color: "#334155", fontSize: 11 }}>
                    <div><b>Vendor:</b> {batch.production_vendor_name || "—"}</div>
                    <div><b>Warehouse:</b> {batch.warehouse_name || "—"}</div>
                  </div>
                </div>
              ) : isRequestedProd ? (
                <div
                  style={{
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderRadius: 6,
                    padding: "7px 9px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    fontSize: 11.5,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#b45309", fontWeight: 700 }}>
                    <Clock size={13} color="#d97706" /> Production Details Requested
                  </div>
                  <span style={{ color: "#78350f", fontSize: 11, lineHeight: 1.4 }}>
                    Awaiting Production team to assign Vendor &amp; Warehouse.
                  </span>
                </div>
              ) : null}

              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  className="outline-btn compact"
                  style={{ color: "#b45309", borderColor: "#fde68a", background: "#fffbeb", fontSize: 11, padding: "4px 8px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveBack();
                  }}
                  disabled={isBusy}
                  data-testid={`bulk-action-move-back-${batch.batch_id}`}
                >
                  <RotateCcw size={11} /> Move Back
                </button>

                {isConfirmedProd ? (
                  <button
                    type="button"
                    className="primary-btn wide"
                    style={{ flex: 1, fontSize: 11.5, padding: "6px 10px", background: "#4f46e5", borderColor: "#4f46e5", justifyContent: "center" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSendToProduction();
                    }}
                    disabled={isBusy}
                    data-testid={`bulk-action-send-prod-${batch.batch_id}`}
                  >
                    <Send size={13} /> Send to Production
                  </button>
                ) : isInitialApproved ? (
                  <button
                    type="button"
                    className="primary-btn wide"
                    style={{ flex: 1, fontSize: 11.5, padding: "6px 10px", background: "#0284c7", borderColor: "#0284c7", justifyContent: "center" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestProduction();
                    }}
                    disabled={isBusy}
                    data-testid={`bulk-action-request-prod-${batch.batch_id}`}
                  >
                    <Factory size={13} /> Request for Production
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer Open Workspace Button */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6, paddingTop: 5, borderTop: "1px solid #f1f5f9" }}>
        <button
          type="button"
          className="outline-btn compact"
          onClick={(e) => {
            e.stopPropagation();
            onOpenDesign(batch.batch_id);
          }}
          style={{ width: "100%", justifyContent: "center", height: 26, fontSize: 11 }}
          data-testid={`open-batch-design-btn-${batch.batch_id}`}
        >
          Open Bulk Workspace <ArrowRight size={11} style={{ marginLeft: 4 }} />
        </button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Stage-Specific Kanban Card
// -------------------------------------------------------------
function StageSpecificCard({
  order,
  stage,
  canEdit,
  user,
  isBusy,
  isDragging,
  onDragStart,
  onDragEnd,
  onOpenOrder,
  onOpenDesign,
  onOpenAssign,
  onStartDesign,
  onSubmitApproval,
  onApprove,
  onRequestRevision,
  onRequestProduction,
  onSendToProduction,
}) {
  const isUrgent = order.priority === "Urgent";
  const isHigh = order.priority === "High";

  const isOverdue = useMemo(() => {
    if (!order.required_delivery_date) return false;
    const deadline = new Date(order.required_delivery_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return deadline < today;
  }, [order.required_delivery_date]);

  const handleCardClick = () => {
    if (onOpenDesign) {
      onOpenDesign(order.order_id);
    } else if (onOpenOrder) {
      onOpenOrder(order.order_id);
    }
  };

  const hasVendor = Boolean(order.production_vendor_name || order.assigned_producer);
  const hasWarehouse = Boolean(order.warehouse_name);
  const isConfirmed = order.production_request_status === "confirmed" && (hasVendor || hasWarehouse);
  const isRequested = !isConfirmed && (order.production_request_status === "requested" || order.production_request_status === "pending");
  const isInitialApproved = !isConfirmed && !isRequested;

  return (
    <div
      className={`design-kanban-card ${isDragging ? "dragging" : ""} ${isBusy ? "busy-card" : ""}`}
      draggable={canEdit && !isBusy}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={handleCardClick}
      style={{ cursor: "pointer", padding: "12px 13px" }}
      data-testid={`design-card-${order.order_id}`}
    >
      {/* Top Row: Order ID & Priority & Version */}
      <div className="card-top-row">
        <span
          className="order-id-badge"
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
          title="Click to view dedicated design workspace"
          data-testid={`card-id-${order.order_id}`}
        >
          #{order.order_id}
        </span>
        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
          <span
            className={`badge ${isUrgent ? "red" : isHigh ? "amber" : "blue"}`}
            style={{ fontSize: 10, padding: "2px 6px" }}
          >
            {order.priority}
          </span>
          {stage !== "New" && order.design_version > 0 && (
            <span className="badge purple" style={{ fontSize: 10, padding: "2px 6px" }}>
              V{order.design_version}
            </span>
          )}
        </div>
      </div>

      {/* Product Name & Quantity Line */}
      <div className="card-client-product" style={{ gap: 2 }}>
        <div className="product-line">
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", lineHeight: 1.3 }}>
            {order.product}
          </span>
          <span className="qty-pill">{(order.quantity || 0).toLocaleString()} units</span>
        </div>
        <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 500 }}>
          {order.client_name}
        </div>
      </div>

      {/* Assigned Designer */}
      <div className="card-designer-row">
        <small className="muted" style={{ fontSize: 11 }}>Designer:</small>
        <button
          className="designer-pill"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenAssign();
          }}
          title="Click to reassign"
          data-testid={`card-designer-${order.order_id}`}
          style={{ fontSize: 11, padding: "2px 8px" }}
        >
          <span className="designer-avatar" style={{ width: 17, height: 17, fontSize: 9.5 }}>
            {(order.assigned_designer || "D").charAt(0).toUpperCase()}
          </span>
          <span>{order.assigned_designer || "Unassigned"}</span>
        </button>
      </div>

      {/* Meta Row: Deadline (Clean) */}
      {order.required_delivery_date && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "#64748b" }}>
          <Calendar size={12} color={isOverdue ? "#dc2626" : "#64748b"} />
          <span className={`deadline-text ${isOverdue ? "overdue" : ""}`}>
            Due: {order.required_delivery_date}
          </span>
        </div>
      )}

      {/* =======================================================
          STAGE 1: NEW CARD (Clean Start action)
         ======================================================= */}
      {stage === "New" && canEdit && (
        <div className="card-actions-footer" style={{ marginTop: 2, paddingTop: 6 }}>
          <button
            className="primary-btn wide"
            style={{ fontSize: 11.5, padding: "6px 12px", background: "#0284c7", borderColor: "#0284c7", justifyContent: "center" }}
            onClick={(e) => {
              e.stopPropagation();
              onStartDesign();
            }}
            disabled={isBusy}
            data-testid={`action-start-${order.order_id}`}
          >
            Start Design <ArrowRight size={13} />
          </button>
        </div>
      )}

      {/* =======================================================
          STAGE 2: IN PROGRESS CARD (Clean Submit action)
         ======================================================= */}
      {stage === "In Progress" && canEdit && (
        <div className="card-actions-footer" style={{ marginTop: 2, paddingTop: 6 }}>
          <button
            className="primary-btn wide"
            style={{ fontSize: 11.5, padding: "6px 12px", background: "#7c3aed", borderColor: "#7c3aed", justifyContent: "center" }}
            onClick={(e) => {
              e.stopPropagation();
              onSubmitApproval();
            }}
            disabled={isBusy}
            data-testid={`action-submit-approval-${order.order_id}`}
          >
            Submit for Approval <ArrowRight size={13} />
          </button>
        </div>
      )}

      {/* =======================================================
          STAGE 3: AWAITING APPROVAL CARD (Review & Actions)
         ======================================================= */}
      {stage === "Awaiting Approval" && (
        <>
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "6px 8px", fontSize: 11.5, color: "#92400e" }}>
            <span style={{ fontWeight: 600 }}>Artwork V{order.design_version || 1}</span> submitted for review
          </div>

          {canEdit && (
            <div className="card-actions-footer" style={{ marginTop: 2, paddingTop: 6, display: "flex", gap: 6 }}>
              <button
                className="outline-btn"
                style={{ flex: 1, fontSize: 11.5, padding: "5px 8px", color: "#dc2626", borderColor: "#fca5a5", justifyContent: "center" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestRevision();
                }}
                disabled={isBusy}
                title="Mark Not Approved (Return for revisions)"
                data-testid={`action-revision-${order.order_id}`}
              >
                <RotateCcw size={12} /> No
              </button>
              <button
                className="primary-btn"
                style={{ flex: 1.3, fontSize: 11.5, padding: "5px 8px", background: "#16a34a", borderColor: "#16a34a", justifyContent: "center" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove();
                }}
                disabled={isBusy}
                title="Approve design"
                data-testid={`action-approve-${order.order_id}`}
              >
                <CheckCircle2 size={13} /> Yes
              </button>
            </div>
          )}
        </>
      )}

      {/* =======================================================
          STAGE 4: APPROVED CARD (Request Production / Send to Production)
         ======================================================= */}
      {stage === "Approved" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {/* Approval Info */}
          <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 6, padding: "5px 8px", fontSize: 11.5, color: "#065f46" }}>
            <b>Approved by:</b> {order.approved_by || order.latest_approval?.approver || "Authorized Approver"}
          </div>

          {/* Production Request / Confirmation State */}
          {isConfirmed ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "7px 9px", fontSize: 11.5 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#166534", fontWeight: 700, marginBottom: 3 }}>
                  <CheckCircle2 size={13} color="#16a34a" /> Production Details Confirmed
                </div>
                <div style={{ color: "#334155", fontSize: 11 }}>
                  <div><b>Vendor:</b> {order.production_vendor_name || order.assigned_producer || "—"}</div>
                  <div><b>Warehouse:</b> {order.warehouse_name || "—"}</div>
                </div>
              </div>

              {canEdit && (
                <button
                  className="primary-btn wide"
                  style={{ fontSize: 11.5, padding: "6px 12px", background: "#4f46e5", borderColor: "#4f46e5", justifyContent: "center" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSendToProduction && onSendToProduction();
                  }}
                  disabled={isBusy}
                  data-testid={`action-send-prod-${order.order_id}`}
                >
                  <Send size={13} /> Send to Production
                </button>
              )}
            </div>
          ) : isRequested ? (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "7px 9px", fontSize: 11.5 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#b45309", fontWeight: 700, marginBottom: 2 }}>
                <Clock size={13} color="#d97706" /> Production Details Requested
              </div>
              <div style={{ color: "#78350f", fontSize: 11, lineHeight: 1.4 }}>
                Awaiting Production team to assign Vendor &amp; Warehouse.
              </div>
            </div>
          ) : isInitialApproved && canEdit ? (
            <button
              className="primary-btn wide"
              style={{ fontSize: 11.5, padding: "6px 12px", background: "#0284c7", borderColor: "#0284c7", justifyContent: "center" }}
              onClick={(e) => {
                e.stopPropagation();
                onRequestProduction && onRequestProduction();
              }}
              disabled={isBusy}
              data-testid={`action-request-prod-${order.order_id}`}
            >
              <Factory size={13} /> Request for Production
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}



// -------------------------------------------------------------
// Assign Designer Dialog
// -------------------------------------------------------------
export function AssignDesignerDialog({ order, designers, user, onClose, onConfirm }) {
  const isEligible = designers.some((d) => d.name === order.assigned_designer);
  const [selectedDesigner, setSelectedDesigner] = useState(isEligible ? order.assigned_designer : "");
  const [customName, setCustomName] = useState("");
  const [useCustom, setUseCustom] = useState(false);

  const handleSave = (e) => {
    e.preventDefault();
    const finalName = useCustom ? customName.trim() : selectedDesigner;
    if (!finalName) {
      toast.error("Please enter or select a designer name");
      return;
    }
    onConfirm(finalName);
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }}>
      <form
        className="modal"
        onSubmit={handleSave}
        style={{ maxWidth: 420 }}
        data-testid="assign-designer-modal"
      >
        <div className="modal-head">
          <div>
            <div className="small-label">ASSIGNMENT</div>
            <h2>Assign Designer</h2>
            <p className="muted">Order #{order.order_id} · {order.product}</p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div style={{ marginTop: 16 }}>
          {!useCustom ? (
            <label className="full">
              Select Designer
              <select
                value={selectedDesigner}
                onChange={(e) => setSelectedDesigner(e.target.value)}
                data-testid="designer-select-dropdown"
                required
              >
                <option value="">-- Choose Designer --</option>
                {designers.map((d) => (
                  <option key={d.user_id || d.email} value={d.name}>
                    {d.name} ({d.email})
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="full">
              Designer Name
              <input
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Enter designer name"
                required
                data-testid="custom-designer-input"
              />
            </label>
          )}

          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              className="outline-btn compact"
              onClick={() => setUseCustom(!useCustom)}
              style={{ fontSize: 12 }}
            >
              {useCustom ? "← Choose from team list" : "+ Enter custom / external designer name"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
          <button type="button" className="outline-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary-btn" data-testid="save-designer-button">
            Assign & Proceed <ChevronRight size={16} />
          </button>
        </div>
      </form>
    </div>
  );
}

// -------------------------------------------------------------
// Vendor Selection Dialog (Required before sending to Production)
// -------------------------------------------------------------
function VendorSelectionDialog({ order, vendors, onClose, onConfirm }) {
  const [selectedVendor, setSelectedVendor] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedVendor) {
      toast.error("Please select a production vendor");
      return;
    }
    const vendor = vendors.find((v) => v.email === selectedVendor || v.vendor_id === selectedVendor);
    onConfirm(vendor?.email || vendor?.vendor_id || selectedVendor, vendor?.name || selectedVendor);
  };

  return (
    <div
      className="modal-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(5px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        zIndex: 10000,
      }}
    >
      <form
        onSubmit={handleSubmit}
        data-testid="vendor-selection-modal"
        style={{
          background: "#ffffff",
          width: "min(520px, 95vw)",
          borderRadius: "16px",
          boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(226, 232, 240, 0.8)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "linear-gradient(to right, #fafafa, #ffffff)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "42px",
                height: "42px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
                color: "#4f46e5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(79, 70, 229, 0.12)",
                flexShrink: 0,
              }}
            >
              <Factory size={22} />
            </div>
            <div>
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.8px",
                  color: "#4f46e5",
                  textTransform: "uppercase",
                  marginBottom: "2px",
                }}
              >
                Send to Production
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#0f172a",
                  lineHeight: 1.2,
                }}
              >
                Select Production Vendor
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f1f5f9";
              e.currentTarget.style.color = "#0f172a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#ffffff";
              e.currentTarget.style.color = "#64748b";
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Order Header Badge Strip */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              fontSize: "13px",
            }}
          >
            <span style={{ fontWeight: 700, color: "#1e293b" }}>
              Order #{order.order_id}
            </span>
            <span style={{ color: "#475569", fontWeight: 600 }}>
              {order.client_name}
            </span>
          </div>

          {/* Workflow Info Alert */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "10px",
              padding: "12px 14px",
              color: "#1e40af",
              fontSize: "13px",
              lineHeight: 1.45,
            }}
          >
            <CheckCircle2 size={17} color="#2563eb" style={{ flexShrink: 0, marginTop: "1px" }} />
            <div>
              <span style={{ fontWeight: 600 }}>Design Approved: </span>
              This order will move to the <b>Production Queue</b> on the shop floor under the selected vendor.
            </div>
          </div>

          {/* Vendor Dropdown */}
          <div>
            <label
              style={{
                display: "block",
                fontWeight: 600,
                fontSize: "13.5px",
                color: "#1e293b",
                marginBottom: "8px",
              }}
            >
              Production Vendor / Shop Floor Team
              <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
            </label>

            <div style={{ position: "relative" }}>
              <select
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value)}
                required
                data-testid="vendor-select-dropdown"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  paddingRight: "36px",
                  fontSize: "14px",
                  fontWeight: 500,
                  borderRadius: "10px",
                  border: "1.5px solid #cbd5e1",
                  background: "#ffffff",
                  color: selectedVendor ? "#0f172a" : "#64748b",
                  cursor: "pointer",
                  outline: "none",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  appearance: "none",
                  WebkitAppearance: "none",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#6366f1";
                  e.target.style.boxShadow = "0 0 0 3px rgba(99, 102, 241, 0.15)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#cbd5e1";
                  e.target.style.boxShadow = "0 1px 2px rgba(0,0,0,0.04)";
                }}
              >
                <option value="">-- Select Production Vendor --</option>
                {vendors.map((v) => (
                  <option key={v.email || v.vendor_id} value={v.email || v.vendor_id}>
                    {v.name} {v.email ? `(${v.email})` : ""}
                  </option>
                ))}
              </select>

              {/* Custom select chevron */}
              <div
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  color: "#64748b",
                }}
              >
                <ChevronRight size={16} style={{ transform: "rotate(90deg)" }} />
              </div>
            </div>

            {vendors.length === 0 ? (
              <div
                style={{
                  marginTop: "8px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  background: "#fffbeb",
                  border: "1px solid #fef3c7",
                  fontSize: "12px",
                  color: "#b45309",
                }}
              >
                <b>No production vendors found.</b> Please add vendors in Production Resources.
              </div>
            ) : (
              <small style={{ display: "block", marginTop: "6px", fontSize: "12px", color: "#64748b" }}>
                Select who will execute manufacturing for this order.
              </small>
            )}
          </div>

          {/* Order Specs Summary Card */}
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "14px 16px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.6px",
                color: "#64748b",
                textTransform: "uppercase",
                marginBottom: "10px",
              }}
            >
              Order Specifications
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px 16px",
                fontSize: "13px",
              }}
            >
              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: "11.5px" }}>Product</span>
                <span style={{ fontWeight: 600, color: "#0f172a" }}>{order.product || "—"}</span>
              </div>
              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: "11.5px" }}>Quantity</span>
                <span style={{ fontWeight: 600, color: "#0f172a" }}>
                  {(order.quantity || 0).toLocaleString()} units
                </span>
              </div>
              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: "11.5px" }}>Required Due Date</span>
                <span style={{ fontWeight: 600, color: "#0f172a" }}>
                  {order.required_delivery_date || "—"}
                </span>
              </div>
              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: "11.5px" }}>Approval</span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    fontWeight: 600,
                    color: "#059669",
                    background: "#ecfdf5",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                >
                  {order.approval_type || "Approved"} ✓
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: "16px 24px",
            background: "#f8fafc",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "10px 18px",
              borderRadius: "10px",
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#475569",
              fontSize: "13.5px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f1f5f9";
              e.currentTarget.style.borderColor = "#94a3b8";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#ffffff";
              e.currentTarget.style.borderColor = "#cbd5e1";
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!selectedVendor || vendors.length === 0}
            data-testid="confirm-vendor-button"
            style={{
              padding: "10px 22px",
              borderRadius: "10px",
              border: "none",
              background: !selectedVendor || vendors.length === 0
                ? "#c7d2fe"
                : "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
              color: "#ffffff",
              fontSize: "13.5px",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: !selectedVendor || vendors.length === 0 ? "not-allowed" : "pointer",
              boxShadow: !selectedVendor || vendors.length === 0
                ? "none"
                : "0 4px 14px rgba(79, 70, 229, 0.35)",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              if (selectedVendor && vendors.length > 0) {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 6px 18px rgba(79, 70, 229, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = !selectedVendor || vendors.length === 0
                ? "none"
                : "0 4px 14px rgba(79, 70, 229, 0.35)";
            }}
          >
            <Send size={15} /> Send to Production
          </button>
        </div>
      </form>
    </div>
  );
}

